import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar } from "@/components/shared/Avatar";
import PoLinkStatus from "@/components/customers/PoLinkStatus";
import CustomerHeroActions from "@/components/customers/CustomerHeroActions";
import CustomerGeekStrip from "@/components/customers/CustomerGeekStrip";
import CustomerLogo from "@/components/customers/CustomerLogo";
import CustomerStatusPill from "@/components/customers/CustomerStatusPill";
import CustomerStatusOverrideDialog from "@/components/customers/CustomerStatusOverrideDialog";
import CustomerOwnersDialog from "@/components/customers/CustomerOwnersDialog";
import CustomerLogoDialog from "@/components/customers/CustomerLogoDialog";
import { useCustomerStatus } from "@/hooks/use-customer-status";
import { useCustomerOwners } from "@/hooks/use-customer-owners";
import { useCustomerContacts } from "@/hooks/use-customer-contacts";
import { useCustomerCompanyInfo } from "@/hooks/use-customer-company-info";
import { useCustomerLeadOrigin } from "@/hooks/use-customer-lead-origin";
import { useMonitoringSources } from "@/hooks/use-monitoring-sources";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { hasMinRole } from "@/lib/constants";
import type { CustomerDetail } from "@/hooks/use-customer-detail";
import { customerSinceYear, lastPurchase } from "@/lib/customer-kpi-calc";
import { formatDate, formatDateShort, formatNOK } from "@/lib/format";
import type { DBProject } from "@/lib/db-types";

interface CustomerHeroProps {
  customer: CustomerDetail;
  projects: DBProject[];
  /** Åpner «Send e-post» for en kontaktperson. */
  onSendEmail: (contactId: string | null) => void;
}

/** Én celle i faktagridet: etikett over verdi, med en dempet linje under. */
function Fact({ label, children, sub }: { label: string; children: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
      {sub && <dd className="truncate text-xs text-muted-foreground">{sub}</dd>}
    </div>
  );
}

/** Første fornavn i en liste, «Sigurd og Christer». */
function firstNames(names: string[]): string {
  const firsts = names.map((n) => n.split(" ")[0]).filter(Boolean);
  if (firsts.length <= 1) return firsts[0] ?? "";
  return `${firsts.slice(0, -1).join(", ")} og ${firsts[firsts.length - 1]}`;
}

/** «kjølner sep 2028» uten dag. */
function monthYear(iso: string): string {
  return new Date(iso).toLocaleDateString("nb-NO", { month: "short", year: "numeric" });
}

/**
 * Toppen av kundekort v3: logo og navn med nettside, org.nr og «kunde siden»
 * under, et faktagrid (status, eier, kontakt, siste kjøp), knapperaden og
 * GEEK-stripen til høyre. Hver del laster for seg selv.
 */
export default function CustomerHero({ customer, projects, onSendEmail }: CustomerHeroProps) {
  const { data: status, isLoading: statusLoading } = useCustomerStatus(customer.id);
  const { data: owners = [], isLoading: ownersLoading } = useCustomerOwners(customer.id);
  const { data: contacts = [] } = useCustomerContacts(customer.id);
  const signaler = useFeatureFlag("feature_crm_signaler");
  const leadsEnabled = useFeatureFlag("feature_crm_leads_moter");
  const { data: info } = useCustomerCompanyInfo(customer.id, { enabled: signaler && customer.is_company !== false });
  const { data: leadOrigin } = useCustomerLeadOrigin(customer.id, { enabled: leadsEnabled });
  const { data: sources = [] } = useMonitoringSources(customer.id, { enabled: signaler });
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [ownersOpen, setOwnersOpen] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);
  const { activeUser } = useImpersonation();
  // VIB-2935: alle ansatte kan bytte logo, ikke ekstern eller skjerm.
  const canEditLogo = hasMinRole(activeUser?.role ?? "external", "user");

  const primary = contacts.find((c) => c.is_primary && c.is_active && !c.anonymized_at) ?? null;
  const since = customerSinceYear(projects);
  const purchase = lastPurchase(projects.map((p) => ({ ...p, is_internal: p.is_internal, project_status: p.project_status })));
  // Brønnøysund først; ellers nettsiden fra overvåkningen, som er eneste kilde
  // for utenlandske kunder og kunder uten hjemmeside i registeret.
  const websiteUrl = info?.website ?? sources.find((s) => s.source === "website" && s.identifier)?.identifier ?? null;
  const website = websiteUrl?.replace(/^https?:\/\//, "").replace(/\/$/, "") ?? null;
  const subline = [
    website ? <a key="web" href={websiteUrl!} target="_blank" rel="noreferrer" className="hover:underline">{website}</a> : null,
    customer.organization_number ? `org.nr ${customer.organization_number}` : null,
    since != null ? `kunde siden ${since}` : null,
  ].filter(Boolean);
  const ownerNames = owners.map((o) => o.user?.full_name ?? "").filter(Boolean);

  return (
    <section aria-label="Kundeoversikt" className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,30rem)]">
      <div className="flex min-w-0 flex-col gap-5 border-[1.5px] border-foreground bg-card px-6 py-5">
        <div className="flex items-center gap-4">
          {canEditLogo ? (
            <button
              type="button"
              className="shrink-0 rounded-none transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Endre logo"
              title="Endre logo"
              onClick={() => setLogoOpen(true)}
            >
              <CustomerLogo name={customer.name} logoUrl={customer.logo_url} />
            </button>
          ) : (
            <CustomerLogo name={customer.name} logoUrl={customer.logo_url} />
          )}
          <div className="min-w-0 space-y-1">
            <h1 className="page-title !mb-0 leading-tight">{customer.name}</h1>
            <p className="text-sm text-muted-foreground">
              {subline.map((part, i) => (
                <span key={i}>{i > 0 ? " · " : ""}{part}</span>
              ))}
              {leadOrigin && (
                <span> · konvertert fra lead {leadOrigin.list_name}{leadOrigin.converted_at ? ` ${formatDate(leadOrigin.converted_at)}` : ""}</span>
              )}
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 border-t border-border pt-4 md:grid-cols-4">
          <Fact
            label="Status"
            sub={
              <>
                {status ? (status.is_overridden ? "overstyrt" : status.cools_at ? `kjølner ${monthYear(status.cools_at)}` : "aldri fått tilbud") : ""}
                {status ? " · " : ""}
                <Button variant="link" size="sm" className="h-auto p-0 text-xs" aria-label="Overstyr kundestatus" onClick={() => setOverrideOpen(true)}>
                  overstyr
                </Button>
              </>
            }
          >
            {statusLoading ? <Skeleton className="h-6 w-20" /> : status ? <CustomerStatusPill status={status.effective_status} punk /> : DASH}
          </Fact>

          <Fact
            label="Eier"
            sub={
              <>
                {ownersLoading ? "" : owners.length === 0 ? "ingen eier ennå · " : ownerNames.length > 1 ? `${ownerNames.map((n) => n.split(" ").slice(1).join(" ")).filter(Boolean).join(" og ")} · ` : ""}
                <Button variant="link" size="sm" className="h-auto p-0 text-xs" aria-label="Endre eiere" onClick={() => setOwnersOpen(true)}>
                  endre
                </Button>
              </>
            }
          >
            {ownersLoading ? (
              <Skeleton className="h-6 w-24" />
            ) : owners.length === 0 ? (
              <span className="text-muted-foreground">Ingen</span>
            ) : (
              <span className="flex items-center gap-2">
                <ul className="flex items-center" aria-label="Kundeeiere">
                  {owners.map((o, i) => (
                    <li key={o.id} title={o.user?.full_name ?? undefined} className={i > 0 ? "-ml-2" : ""}>
                      <Avatar name={o.user?.full_name} avatarUrl={o.user?.avatar_url} size="sm" className="ring-2 ring-card" />
                    </li>
                  ))}
                </ul>
                <span className="truncate">{ownerNames.length > 1 ? firstNames(ownerNames) : ownerNames[0]}</span>
              </span>
            )}
          </Fact>

          <Fact
            label="Kontakt"
            sub={
              primary ? (
                <>
                  {primary.title ? `${primary.title} · ` : ""}
                  {primary.email ? (
                    <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => onSendEmail(primary.id)}>
                      send e-post
                    </Button>
                  ) : "ingen e-post"}
                </>
              ) : "legg til under Kontaktpersoner"}
          >
            {primary ? primary.full_name : <span className="text-muted-foreground">Ingen hovedkontakt</span>}
          </Fact>

          <Fact
            label="Siste kjøp"
            sub={purchase ? (
              <Link to={`/prosjekter/${purchase.projectId}`} className="hover:underline">
                {purchase.name}{purchase.contractKr != null ? ` · ${formatNOK(purchase.contractKr)}` : ""}
              </Link>
            ) : "ingen prosjekter ennå"}
          >
            {purchase ? formatDateShort(purchase.at) + " " + new Date(purchase.at).getFullYear() : <span className="text-muted-foreground">Aldri</span>}
          </Fact>
        </dl>

        <PoLinkStatus customerId={customer.id} poCustomerId={customer.poweroffice_customer_id} />
        <CustomerHeroActions customer={customer} onSendEmail={onSendEmail} />
      </div>

      <CustomerGeekStrip customerId={customer.id} projects={projects} />

      <CustomerStatusOverrideDialog
        open={overrideOpen}
        onOpenChange={setOverrideOpen}
        customerId={customer.id}
        current={status ?? null}
      />
      <CustomerOwnersDialog open={ownersOpen} onOpenChange={setOwnersOpen} customerId={customer.id} />
      <CustomerLogoDialog
        open={logoOpen}
        onOpenChange={setLogoOpen}
        customerId={customer.id}
        customerName={customer.name}
        hasLogo={!!customer.logo_url}
        suggestedWebsite={info?.website ?? null}
      />
    </section>
  );
}

const DASH = "–";
